import { non_quantity_design_uuids } from "@/utils/subassemblies";
import { backend_api_v2, paginated_factory_v2 } from '/src/utils/apiv2'
import base from "./base"
import { defineStore } from 'pinia'
import { useCompanyStore } from "/src/stores/company"
import { useSelectedStore } from "/src/stores/selected"
import { useSessionStore } from "/src/stores/session"
import { useAppStore } from './app'
import { projectPhases } from "/src/utils/phase_keys"
// import bus from '/src/utils/event_bus'
// import router from '/src/router'

const LASTS = {}

// Batched hint loading
let _hint_queue_assemblies = []
let _hint_queue_modules = []
let _hint_timeout = null

// THIS MODULE SHOULD HOLD PROJECT/STDLIB CONSTUCTIBLES. NOT MORE NOT LESS.

function constructible_sorting_callback(a, b) {
    const session_store = useSessionStore()
    let sta = session_store.selected_station_name

    // First of all, readies go last, no readies first.
    if ((a.stations_status[sta] != "ready" && a.status != "ready") &&
        (b.stations_status[sta] == "ready" || b.status == "ready")) {
        return -1
    }
    if (
        (a.stations_status[sta] == "ready" || a.status == "ready") &&
        (b.stations_status[sta] != "ready" && b.status != "ready")) {
        return 1
    }

    // Compare dates then
    const dateA = new Date(a.scheduled_prod_date);
    const dateB = new Date(b.scheduled_prod_date);
    if (dateA < dateB) return -1;
    if (dateA > dateB) return 1;

    // If dates are equal, and status equivalent, compare priorities
    return a.priority - b.priority;
}

export const useConstructiblesStore = defineStore('constructibles', {
    state: () => ({
        // Designs
        module_designs: [],
        assembly_designs: [],
        part_designs: [],

        // STDLIB designs
        stdlib_module_designs: [],
        stdlib_assembly_designs: [],

        // Actuals
        batches: [],
        modules: [],
        assemblies: [],
        part_batches: [],
        parts: [],

        // Multipanel
        multipanel_groups: [],

        // Cache for list hints (uuid -> hint string)
        list_hints: {},
    }),
    getters: {   // LISTS
        allDesigns: state => [...state.module_designs, ...state.assembly_designs,],
        allActuals: state => [...state.modules, ...state.assemblies,],

        // Cached lookup Maps for O(1) access in list items
        designsByUuid: state => {
            const map = new Map()
            for (const d of state.module_designs) map.set(d.uuid, { ...d, level: "module" })
            for (const d of state.stdlib_module_designs) map.set(d.uuid, { ...d, level: "module" })
            for (const d of state.assembly_designs) map.set(d.uuid, { ...d, level: "assembly" })
            for (const d of state.stdlib_assembly_designs) map.set(d.uuid, { ...d, level: "assembly" })
            return map
        },
        // Constructibles keyed by uuid, both levels. Built once per change
        // instead of a .find() per lookup, which is what the multipanel start
        // needs when it has to resolve a group's members before gating them.
        actualsByUuid: state => {
            const map = new Map()
            for (const actual of state.assemblies) map.set(actual.uuid, actual)
            for (const actual of state.modules) map.set(actual.uuid, actual)
            return map
        },
        batchesByUuid: state => {
            const map = new Map()
            for (const b of state.batches) map.set(b.uuid, b)
            return map
        },
        // How many units exist per (design, batch) slot, per level — the same slot
        // the quantities matrix plans and the generator writes. Kept here, beside
        // the units it counts, so a keystroke in the matrix does not re-index
        // thousands of assemblies: this only invalidates when the units change.
        // A unit with no batch collapses to the empty key, exactly as countKey does.
        actualsCountByDesignBatch: state => {
            const index = { assembly: new Map(), module: new Map() }
            const fill = (map, actuals) => {
                for (const actual of actuals) {
                    const key = `${actual.design}|${actual.batch ?? ""}`
                    map.set(key, (map.get(key) || 0) + 1)
                }
            }
            fill(index.assembly, state.assemblies)
            fill(index.module, state.modules)
            return index
        },
        // design uuid -> bruto area. Used by the quantities m2 totals, which used
        // to re-spread every design and then .find() one per count object.
        brutoAreaByDesign: state => {
            const map = new Map()
            const sources = [
                state.module_designs, state.stdlib_module_designs,
                state.assembly_designs, state.stdlib_assembly_designs,
            ]
            for (const source of sources) {
                for (const d of source) map.set(d.uuid, d.meta?.metrics?.bruto || 0)
            }
            return map
        },
        // "<level>|<name>" -> highest revision. Lets a row decide "am I the latest
        // revision?" with one lookup instead of re-deriving the whole design list.
        maxRevisionByLevelAndName: state => {
            const map = new Map()
            const sources = [
                ["module", state.module_designs], ["module", state.stdlib_module_designs],
                ["assembly", state.assembly_designs], ["assembly", state.stdlib_assembly_designs],
            ]
            for (const [level, source] of sources) {
                for (const d of source) {
                    const key = `${level}|${d.name}`
                    const known = map.get(key)
                    if (known === undefined || d.revision > known) map.set(key, d.revision)
                }
            }
            return map
        },
        maxPriorityByBatch: state => {
            const map = new Map()
            for (const a of state.modules) {
                const cur = map.get(a.batch) || 0
                if (a.priority > cur) map.set(a.batch, a.priority)
            }
            for (const a of state.assemblies) {
                const cur = map.get(a.batch) || 0
                if (a.priority > cur) map.set(a.batch, a.priority)
            }
            return map
        },
        maxPriorityByDate: state => {
            const map = new Map()
            for (const a of state.modules) {
                const date = a.scheduled_prod_date || ""
                const cur = map.get(date) || 0
                if (a.priority > cur) map.set(date, a.priority)
            }
            for (const a of state.assemblies) {
                const date = a.scheduled_prod_date || ""
                const cur = map.get(date) || 0
                if (a.priority > cur) map.set(date, a.priority)
            }
            return map
        },
        maxRevisionByDesignName: state => {
            const map = new Map()
            const allDesigns = [
                ...state.module_designs, ...state.stdlib_module_designs,
                ...state.assembly_designs, ...state.stdlib_assembly_designs
            ]
            for (const d of allDesigns) {
                const cur = map.get(d.name) || 0
                if (d.revision > cur) map.set(d.name, d.revision)
            }
            return map
        },
        myList() {
            return function (level) {
                const app_store = useAppStore()
                const selected_store = useSelectedStore()
                let actuals = this.annotatedActualsFromLevel(level)
                const by_date_active = app_store.byDateModeActive

                // ONLY BY_DATE
                const show_delayed = selected_store.show_delayed_constructibles
                const max_shown_is_set = selected_store.max_shown_is_set
                const max_shown_constructibles = selected_store.max_shown_constructibles

                // ONLY BY_PROJECT
                const show_ready = selected_store.show_ready_constructibles

                // BOTH BY_DATE AND BY_PROJECT
                // pass


                let my_list = []

                if (by_date_active) {
                    const my_batches_uuids = this.myBatches.map(b => b.uuid)

                    // Sorted straight by priority (no repeated numbers for single date).
                    my_list = actuals
                        .filter(a => my_batches_uuids.includes(a.batch))
                        .filter(a => show_delayed || a.scheduled_prod_date >= new Date())
                        .sort(constructible_sorting_callback)
                        .slice(0, max_shown_is_set ? max_shown_constructibles : actuals.length)
                } else {
                    // Narrowed by the left menu's batch filter, which sets the
                    // batch (and its phase) or a phase on its own.
                    const phase_filter = selected_store.viewer_phase_filter
                    const batch_filter = selected_store.viewer_batch_filter
                    let batches = this.myBatches
                    if (batch_filter) batches = batches.filter(b => b.uuid == batch_filter)
                    else if (phase_filter) batches = batches.filter(b => b.phase_uuid == phase_filter)

                    // Sorted by phase-batch-priority.
                    let sorted_list = []
                    batches.forEach(batch => {
                        sorted_list.push(
                            ...actuals
                                .filter(con => con.batch == batch.uuid)
                                .filter(a => show_ready || a.status != "ready" || a.uuid == selected_store.selectedConstUuid)
                                .sort(constructible_sorting_callback)
                        )
                    })

                    // The constructible open in the viewer stays listed even
                    // when its batch was filtered out, so the operator never
                    // loses their place. Same rule the ready filter above
                    // already applies to a ready constructible.
                    const open_uuid = selected_store.selectedConstUuid
                    if (open_uuid && !sorted_list.some(c => c.uuid == open_uuid)) {
                        const open = actuals.find(c => c.uuid == open_uuid)
                        if (open) sorted_list.push(open)
                    }
                    my_list = sorted_list
                }
                return my_list
            }
        },
        myFirst: () => function (level) {
            const session_store = useSessionStore()
            let sta = session_store.selected_station_name
            let list = this.myList(level).filter(con => con.stations_status?.[sta] != "ready" && con.status != "ready")
            if (list.length != 0)
                return list[0]
            else return false
        },
        myListNoFirst: () => function (level) {
            const session_store = useSessionStore()
            let sta = session_store.selected_station_name
            let first = this.myFirst(level)
            let no_first = this.myList(level)
                .filter(con => con.uuid != first.uuid)
            let futures = no_first
                .filter(con => con.stations_status?.[sta] != "ready" && con.status != "ready")
            let readies = no_first
                .filter(con => con.stations_status?.[sta] == "ready" || con.status == "ready")
            const my_list = [...futures, ...readies]
            return my_list
        },
        annotatedActualsFromLevel: (state) => (level) => {
            if (level == "assembly") {
                return state.assemblies.map(assd => ({ ...assd, level: "assembly" }));
            } else if (level == "module") {
                return state.modules.map(modd => ({ ...modd, level: "module" }));
            } else {
                return [
                    ...state.modules.map(modd => ({ ...modd, level: "module" })),
                    ...state.assemblies.map(assd => ({ ...assd, level: "assembly" }))
                ];
            }
        },
        // This annotates the class, for when you mix both designs in same list.
        // Cached per level: the annotation copies every design, so calling this
        // repeatedly (once per row, or on every store mutation) used to hand out
        // fresh object identities and invalidate everything downstream.
        annotatedDesignsByLevel: (state) => {
            const module = state.module_designs
                .concat(state.stdlib_module_designs)
                .map(modd => ({ ...modd, level: "module" }))
            const assembly = state.assembly_designs
                .concat(state.stdlib_assembly_designs)
                .map(assd => ({ ...assd, level: "assembly" }))
            return { module, assembly, all: [...module, ...assembly] }
        },
        annotatedDesignsFromLevel() {
            const by_level = this.annotatedDesignsByLevel
            return (level) => by_level[level] || by_level.all
        },
        annotatedDesignsLastRevisionsByLevel() {
            const by_level = this.annotatedDesignsByLevel
            const pick_last = (designs) => {
                const latest = new Map()
                for (const d of designs) {
                    const known = latest.get(d.name)
                    if (!known || known.revision < d.revision) latest.set(d.name, d)
                }
                return [...latest.values()]
            }
            return {
                module: pick_last(by_level.module),
                assembly: pick_last(by_level.assembly),
                all: pick_last(by_level.all),
            }
        },
        annotatedDesignsFromLevelOnlyLastRevisions() {
            const by_level = this.annotatedDesignsLastRevisionsByLevel
            return (level) => by_level[level] || by_level.all
        },
        // The designs the quantities matrix does not plan: subassemblies, and
        // every revision of a name whose newest revision is one (see the helper).
        nonQuantityDesignUuids() {
            return non_quantity_design_uuids(this.annotatedDesignsByLevel.assembly)
        },
        // The designs the quantities matrix plans. Both lists leave out the same
        // designs, so the rows and the targets cannot disagree on a name.
        quantityDesignsFromLevel() {
            const from_level = this.annotatedDesignsFromLevel
            const excluded = this.nonQuantityDesignUuids
            return (level) => from_level(level).filter(d => !excluded.has(d.uuid))
        },
        quantityDesignsFromLevelOnlyLastRevisions() {
            const from_level = this.annotatedDesignsFromLevelOnlyLastRevisions
            const excluded = this.nonQuantityDesignUuids
            return (level) => from_level(level).filter(d => !excluded.has(d.uuid))
        },

        // Batch and batch state
        myBatches: (state) => {
            const session_store = useSessionStore()
            const selected_store = useSelectedStore()
            let user_object = session_store.user_object;
            // Position of each phase in the selected project. Company-wide there
            // is none, and the batches keep their priority order.
            const phase_order = new Map(projectPhases(selected_store.selected_project)
                .map(p => [p.uuid, p.order]))
            let production_line = user_object
                ? user_object.auth?.selected_production_line
                : null;
            let batches = [...state.batches]
            if (production_line) {
                batches = batches.filter((b) => b.production_line == production_line);
            }
            batches.sort((a, b) => {
                // Sort by phase and batch
                let idx_phase_a = phase_order.get(a.phase_uuid) ?? -1
                let idx_phase_b = phase_order.get(b.phase_uuid) ?? -1
                if (idx_phase_a == idx_phase_b) return a.priority - b.priority
                else return idx_phase_a - idx_phase_b
            })
            return batches
        },

        // Rows for the viewer's batch filter: myBatches grouped by phase, each
        // batch and each phase carrying its ready/total count.
        //
        // The counts are derived from the loaded constructibles rather than read
        // from the `constructibles_count` / `ready_constructibles_count` the API
        // annotates on a Batch: the operator marks constructibles ready from
        // this very screen, and only the local ones move. `status` is
        // recomputed from them for the same reason, mirroring
        // `BatchSerializer.get_status` so `batchIcon()` keeps working — an empty
        // batch is `waiting`, not `ready`.
        myBatchesByPhase() {
            const totals = new Map()
            const readies = new Map()
            for (const a of this.allActuals) {
                totals.set(a.batch, (totals.get(a.batch) || 0) + 1)
                if (a.status == "ready") readies.set(a.batch, (readies.get(a.batch) || 0) + 1)
            }

            const groups = []
            const by_phase = new Map()
            // Grouped by phase uuid; `phase` is the name to show.
            for (const b of this.myBatches) {
                const phase_uuid = b.phase_uuid || ""
                let group = by_phase.get(phase_uuid)
                if (!group) {
                    group = { phase_uuid, phase: b.phase || "", total: 0, ready: 0, batches: [] }
                    by_phase.set(phase_uuid, group)
                    groups.push(group)
                }
                const total = totals.get(b.uuid) || 0
                const ready = readies.get(b.uuid) || 0
                let status = "waiting"
                if (total && ready == total) status = "ready"
                else if (ready) status = "working"
                group.total += total
                group.ready += ready
                group.batches.push({ ...b, total, ready, status })
            }
            return groups
        },

        // Designs from actuals
        assemblyDesignFromAssembly: (state) => {
            return function (assembly) {
                return [...state.assembly_designs, ...state.stdlib_assembly_designs].find((d) => d.uuid == assembly.design)
            }
        },

        // BY UUID GETTERS
        designNameByUuid() {
            const allDesigns = this.allDesigns
            return function (uuid, include_rev = true) {
                let d = allDesigns.find((d) => d.uuid == uuid)
                if (include_rev) return `${d ? d?.name : ""} rev ${d?.revision}`
                else return `${d ? d?.name : ""}`
            }
        },
        getLevelFromActualUuid: () => function (uuid) {
            let actual = this.annotatedActualsFromLevel("all")
                .find(actual => actual.uuid == uuid)
            return actual?.level || "unknown"
        },


        multipanelGroupsByBatch: state => {
            const map = new Map()
            for (const g of state.multipanel_groups) {
                if (!map.has(g.batch)) map.set(g.batch, [])
                map.get(g.batch).push(g)
            }
            return map
        },

        multipanelGroupByUuid: state => uuid => state.multipanel_groups.find(g => g.uuid == uuid),

        // Map<assembly_uuid, group> — used by the constructibles table to render group rows
        multipanelGroupByAssemblyUuid: state => {
            const map = new Map()
            for (const g of state.multipanel_groups) {
                for (const m of g.memberships || []) {
                    map.set(m.assembly, g)
                }
            }
            return map
        },

        // Boolean flags
        haveModules: state => state.modules.length != 0,
        haveModuleDesigns: state => state.module_designs.length != 0,
        haveAssemblies: state => state.assemblies.length != 0,
        haveAssemblyDesigns: state => state.assembly_designs.length != 0,
        isBatchReadyByUuid() {
            const allActuals = this.allActuals
            return function (uuid) {
                let locked = true;
                let actuals = allActuals.filter(actual => actual.batch == uuid)
                if (actuals.length == 0) return false
                actuals.forEach((actual) => {
                    if (actual.status != "ready") locked = false;
                });
                return locked;
            }
        },
        isStdlibDesign: state => design => {
            const uuid = design?.uuid || design
            return state.stdlib_module_designs.some(d => d?.uuid == uuid) ||
                state.stdlib_assembly_designs.some(d => d?.uuid == uuid)
        },
    },
    actions: {
        // Designs
        setModuleDesigns: base.actions.setter("module_designs"),
        createModuleDesign: base.actions.creator("module_designs"),
        updateModuleDesign: base.actions.updater("module_designs"),
        removeModuleDesign: base.actions.remover("module_designs"),

        setAssemblyDesigns: base.actions.setter("assembly_designs"),
        createAssemblyDesigns: base.actions.creator("assembly_designs"),
        updateAssemblyDesign: base.actions.updater("assembly_designs"),
        removeAssemblyDesigns: base.actions.remover("assembly_designs"),

        // THIS ARE PROJECT-LINKED STDLIB DESIGNS (NOT FILLED WHEN YOU SELECT STDLIB IN PROJECT SELECTOR)
        setStdlibAssemblyDesigns: base.actions.setter("stdlib_assembly_designs"),

        setPartDesigns: base.actions.setter("part_designs"),
        createPartDesign: base.actions.creator("part_designs"),
        updatePartDesign: base.actions.updater("part_designs"),
        removePartDesign: base.actions.remover("part_designs"),

        // Actuals
        setBatches: base.actions.setter("batches"),
        createBatch: base.actions.creator("batches"),
        updateBatch: base.actions.updater("batches"),
        removeBatch: base.actions.remover("batches"),

        setModules: base.actions.setter("modules"),
        createModule: base.actions.creator("modules"),
        updateModule: base.actions.updater("modules"),
        removeModule: base.actions.remover("modules"),

        setAssemblies: base.actions.setter("assemblies"),
        createAssembly: base.actions.creator("assemblies"),
        updateAssembly: base.actions.updater("assemblies"),

        /** Fold fresh derived kit counts into constructibles already loaded.
         *
         * `base.actions.updater` replaces the whole object, and these rows carry
         * only the three derived fields, so they are merged rather than swapped
         * in. `set_ready_count` returns exactly the constructibles its parts
         * moved, which is what keeps the logistics screens honest without a
         * refetch: the kit is derived, so the copies held here go stale the
         * moment a part is marked ready.
         */
        applyKits(rows) {
            if (!rows || !rows.length) return
            const by_level = { assembly: new Map(), module: new Map() }
            rows.forEach(row => by_level[
                row.level === "module" ? "module" : "assembly"
            ].set(row.uuid, row))

            for (const [level, map] of Object.entries(by_level)) {
                if (!map.size) continue
                const key = level === "module" ? "modules" : "assemblies"
                let touched = false
                const next = this[key].map(actual => {
                    const row = map.get(actual.uuid)
                    if (!row) return actual
                    touched = true
                    return {
                        ...actual,
                        kit_total: row.kit_total,
                        kit_ready: row.kit_ready,
                        kit_status: row.kit_status,
                    }
                })
                if (touched) this[key] = next
            }
        },
        removeAssembly: base.actions.remover("assemblies"),

        setPartBatches: base.actions.setter("part_batches"),
        createPartBatch: base.actions.creator("part_batches"),
        updatePartBatch: base.actions.updater("part_batches"),
        removePartBatch: base.actions.remover("part_batches"),

        // THIS IS NOT USED!
        setParts: base.actions.setter("parts"),
        createPart: base.actions.creator("parts"),
        updatePart: base.actions.updater("parts"),
        removePart: base.actions.remover("parts"),


        // GENERAL MULTIPLE DELETION
        multiDeleteByUuids(uuids) {
            // Delete from all lists
            const store_keys = [
                "module_designs",
                "assembly_designs",
                "part_designs",
                "stdlib_module_designs",
                "stdlib_assembly_designs",
                "batches",
                "modules",
                "assemblies",
                "parts",
            ]
            store_keys.forEach((k) => this[k] = this[k].filter(d => !uuids.includes(d.uuid)))
        },

        multiUpdaterByUuids(uuids, payload) {
            const store_keys = [
                "module_designs",
                "assembly_designs",
                "part_designs",
                "stdlib_module_designs",
                "stdlib_assembly_designs",
                "batches",
                "modules",
                "assemblies",
                "parts",
            ]
            store_keys.forEach((k) => {
                let items = this[k]
                items.forEach((item) => {
                    if (uuids.includes(item.uuid)) {
                        Object.assign(item, payload)
                    }
                })
            })
        },


        // Designs backend actions
        loadModuleDesigns: base.actions.loader_pr("constructibles/module_designs/", "ModuleDesigns"),
        loadModuleDesignsByDate: base.actions.loader_date("constructibles/module_designs/", "ModuleDesigns"),
        postModuleDesign: base.actions.poster("constructibles/module_designs/", "ModuleDesign"),
        putModuleDesign: base.actions.putter("constructibles/module_designs/", "ModuleDesign"),
        deleteModuleDesign: base.actions.deleter("constructibles/module_designs/", "ModuleDesign"),
        refreshModuleDesign: base.actions.refresher("constructibles/module_designs/", "ModuleDesign"),
        refreshModuleDesigns: base.actions.multi_refresher("constructibles/module_designs/", "ModuleDesign", true),

        loadAssemblyDesigns: base.actions.loader_pr("constructibles/assembly_designs/", "AssemblyDesigns"),
        loadAssemblyDesignsByDate: base.actions.loader_date("constructibles/assembly_designs/", "AssemblyDesigns"),
        postAssemblyDesign: base.actions.poster("constructibles/assembly_designs/", "AssemblyDesign"),
        putAssemblyDesign: base.actions.putter("constructibles/assembly_designs/", "AssemblyDesign"),
        deleteAssemblyDesign: base.actions.deleter("constructibles/assembly_designs/", "AssemblyDesign"),
        refreshAssemblyDesign: base.actions.refresher("constructibles/assembly_designs/", "AssemblyDesign"),
        refreshAssemblyDesigns: base.actions.multi_refresher("constructibles/assembly_designs/", "AssemblyDesign", true),

        /**
         * Fetch assembly designs by uuid and **merge** them into the store.
         *
         * `refreshAssemblyDesigns` cannot do this. It is a `multi_refresher`,
         * which calls `updateAssemblyDesign` per row, and `base.actions.updater`
         * is a no-op when the uuid is not already in the array. Screens reached
         * without a project — the rack load page, opened by scanning the
         * sticker on a rack — start with an empty store, so a refresher leaves
         * it empty and every panel ends up drawn at the placeholder size.
         *
         * The alternative those screens used was `loadAssemblyDesigns()` with
         * no argument, which cleared the store and then asked for every design
         * in the company.
         *
         * Only the uuids missing from the store are requested.
         *
         * @param {string[]} uuids design uuids
         * @returns {Promise<Array>} the designs that were added
         */
        async fetchAssemblyDesigns(uuids) {
            const wanted = [...new Set((uuids || []).filter(Boolean))]
            if (!wanted.length) return []
            const have = new Set(this.assembly_designs.map(d => d.uuid))
            const missing = wanted.filter(uuid => !have.has(uuid))
            if (!missing.length) return []
            try {
                const { data } = await backend_api_v2.post(
                    "constructibles/assembly_designs/get_by_uuids/", missing)
                const fetched = data || []
                if (fetched.length)
                    this.setAssemblyDesigns([...this.assembly_designs, ...fetched])
                return fetched
            } catch (error) {
                console.warn("Could not fetch assembly designs:", error)
                return []
            }
        },

        // THIS ARE PROJECT-LINKED STDLIB DESIGNS (NOT FILLED WHEN YOU SELECT STDLIB IN PROJECT SELECTOR)
        loadStdlibAssemblyDesigns(stdlib_uuids) {
            const app_store = useAppStore()
            app_store.pushLoading("stdStdlibAssemblyDesigns")

            // Register last call (ensure last called fills)
            LASTS["stdStdlibAssemblyDesigns"] = stdlib_uuids.join(",")

            // Clean previous
            this.setStdlibAssemblyDesigns([])

            // If project is "all", query as null
            let query_pr = stdlib_uuids.join(",")
            return paginated_factory_v2(`constructibles/assembly_designs/?project__in=${query_pr}`)
                .then(({ data }) => {
                    // Accept last
                    if (LASTS["stdStdlibAssemblyDesigns"] == stdlib_uuids.join(","))
                        this.setStdlibAssemblyDesigns(data)
                    // Reject prebounce
                    else console.log("[i] Rejecting loader commit. Not last call.")
                    return data
                })
                .catch(e => {
                    console.log(`Cant get StdlibAssemblyDesigns: ${e}`)
                })
                .finally(() => app_store.popLoading("stdStdlibAssemblyDesigns"))
        },

        // FOR NOW PART DESIGNS ARE NOT USED DIRECTLY
        // loadPartDesigns: base.actions.loader_pr("constructibles/part_designs/", "PartDesigns"),
        // loadPartDesignsByDate: base.actions.loader_date("constructibles/part_designs/", "PartDesigns"),
        // postPartDesign: base.actions.poster("constructibles/part_designs/", "PartDesign"),
        // putPartDesign: base.actions.putter("constructibles/part_designs/", "PartDesign"),
        // deletePartDesign: base.actions.deleter("constructibles/part_designs/", "PartDesign"),

        // Multiple Actions for Designs
        multiDeleteDesigns(uuids) {
            return backend_api_v2.post('constructibles/designs/bulk_delete/', uuids)
                .then(() => this.multiDeleteByUuids(uuids))
                .catch(e => { console.log(`Cant batch delete designs: ${e}`) })
        },
        setDesignAsWaitingForAnalysis(uuids) {
            uuids.forEach(uuid => {
                // Update assembly designs
                let design = this.assembly_designs.find(d => d.uuid === uuid)
                if (design) {
                    let updated = { ...design }
                    if (!updated.meta) updated.meta = {}
                    updated.meta = { ...updated.meta, analysis: "waiting" }
                    this.updateAssemblyDesign(updated)
                    return
                }
                // Update module designs
                design = this.module_designs.find(d => d.uuid === uuid)
                if (design) {
                    let updated = { ...design }
                    if (!updated.meta) updated.meta = {}
                    updated.meta = { ...updated.meta, analysis: "waiting" }
                    this.updateModuleDesign(updated)
                }
            })
        },
        launchAnalysisForDesigns(uuids, regen_part_designs = false) {
            return backend_api_v2.post(
                'constructibles/designs/launch_analysis/',
                { uuids, regen_part_designs },
            )
                .then(() => {
                    console.log(`Launched analysis for: ${uuids} (regen_part_designs=${regen_part_designs})`)
                    this.setDesignAsWaitingForAnalysis(uuids)
                })
                .catch(e => { console.log(`Cant launch analysis: ${e}`) })
        },
        launchAction(endpoint, payload) {
            return backend_api_v2.post(endpoint, payload)
                .then(() => console.log(`Launched action ${payload.action} for ${payload.uuids?.length ?? 0} item(s)`))
                .catch(e => { console.log(`Cant launch action: ${e}`) })
        },

        // Actuals
        // A phase was renamed on the backend, batches included: carry the new
        // name over to the loaded ones without reloading them.
        renamePhaseInBatches(phase_uuid, new_name) {
            for (const b of this.batches) {
                if (b.phase_uuid === phase_uuid) b.phase = new_name
            }
        },
        // The database cascades the delete to the batch's constructibles and
        // multipanel groups. The copies held here go with them, or every screen
        // counting units would keep seeing them until the next load. Throws.
        deleteBatchWithActuals(uuid) {
            return backend_api_v2.delete(`constructibles/batches/${uuid}/`)
                .then(() => {
                    this.removeBatch(uuid)
                    this.assemblies = this.assemblies.filter(a => a.batch !== uuid)
                    this.modules = this.modules.filter(m => m.batch !== uuid)
                    this.multipanel_groups = this.multipanel_groups.filter(g => g.batch !== uuid)
                })
        },
        loadBatches: base.actions.loader_pr("constructibles/batches/", "Batches"),
        loadBatchesByDate: base.actions.loader_date("constructibles/batches/", "Batches"),
        postBatch: base.actions.poster("constructibles/batches/", "Batch"),
        putBatch: base.actions.putter("constructibles/batches/", "Batch"),
        deleteBatch: base.actions.deleter("constructibles/batches/", "Batch"),

        loadModules: base.actions.loader_pr("constructibles/modules/", "Modules"),
        loadModulesByDate: base.actions.loader_date("constructibles/modules/", "Modules"),
        postModule: base.actions.poster("constructibles/modules/", "Module"),
        putModule: base.actions.putter("constructibles/modules/", "Module"),
        deleteModule: base.actions.deleter("constructibles/modules/", "Module"),
        refreshModule: base.actions.refresher("constructibles/modules/", "Module"),
        refreshModules: base.actions.multi_refresher("constructibles/modules/", "Module"),

        loadAssemblies: base.actions.loader_pr("constructibles/assemblies/", "Assemblies"),
        loadAssembliesByDate: base.actions.loader_date("constructibles/assemblies/", "Assemblies"),
        postAssembly: base.actions.poster("constructibles/assemblies/", "Assembly"),
        putAssembly: base.actions.putter("constructibles/assemblies/", "Assembly"),
        deleteAssembly: base.actions.deleter("constructibles/assemblies/", "Assembly"),
        refreshAssembly: base.actions.refresher("constructibles/assemblies/", "Assembly"),
        refreshAssemblies: base.actions.multi_refresher("constructibles/assemblies/", "Assembly"),
        pollAssemblies: base.actions.poller("constructibles/assemblies/", "Assembly", "assemblies"),
        pollModules: base.actions.poller("constructibles/modules/", "Module", "modules"),

        loadPartBatches: base.actions.loader_pr("constructibles/part_batches/", "PartBatches"),
        loadPartBatchesByDate: base.actions.loader_date("constructibles/part_batches/", "PartBatches"),
        postPartBatch: base.actions.poster("constructibles/part_batches/", "PartBatch"),
        putPartBatch: base.actions.putter("constructibles/part_batches/", "PartBatch"),
        deletePartBatch: base.actions.deleter("constructibles/part_batches/", "PartBatch"),

        // THIS IS NOT USED!
        loadParts: base.actions.loader_pr("constructibles/parts/", "Parts"),
        loadPartsByDate: base.actions.loader_date("constructibles/parts/", "Parts"),
        postPart: base.actions.poster("constructibles/parts/", "Part"),
        putPart: base.actions.putter("constructibles/parts/", "Part"),
        deletePart: base.actions.deleter("constructibles/parts/", "Part"),


        // Multiple Actions for Actuals
        // Resolves whether the delete happened, so the caller only drops the
        // rows from its table when they are really gone.
        multiDeleteActuals(uuids) {
            return backend_api_v2.post('constructibles/actuals/bulk_delete/', uuids)
                .then(() => {
                    this.multiDeleteByUuids(uuids)
                    return true
                })
                .catch(e => {
                    console.log(`Cant batch delete actuals: ${e}`)
                    return false
                })
        },
        multiPatchActuals(payload) {
            return backend_api_v2.post('constructibles/actuals/bulk_patch/', payload)
                .then(() => this.multiUpdaterByUuids(payload.uuids, payload.payload))
                .catch(e => { console.log(`Cant batch delete actuals: ${e}`) })
        },

        // Multiple Actions for Designs
        multiPatchDesigns(payload) {
            return backend_api_v2.post('constructibles/designs/bulk_patch/', payload)
                .then(() => this.multiUpdaterByUuids(payload.uuids, payload.payload))
                .catch(e => { console.log(`Cant batch patch designs: ${e}`) })
        },

        // Others
        fetchListHint(uuid, level) {
            // The company setting is the first gate. Without it the endpoint
            // answers `{}` for the companies that have the hint off, nothing
            // gets cached, and every row that mounts queues another POST that
            // can only come back empty again.
            if (!useSessionStore().custom_list_hint) return
            if (this.list_hints[uuid] !== undefined) return
            if (level === "module") {
                if (!_hint_queue_modules.includes(uuid)) _hint_queue_modules.push(uuid)
            } else {
                if (!_hint_queue_assemblies.includes(uuid)) _hint_queue_assemblies.push(uuid)
            }
            if (_hint_timeout) clearTimeout(_hint_timeout)
            _hint_timeout = setTimeout(() => this._flushHintQueue(), 200)
        },
        _flushHintQueue() {
            const ass_uuids = [..._hint_queue_assemblies]
            const mod_uuids = [..._hint_queue_modules]
            _hint_queue_assemblies = []
            _hint_queue_modules = []
            _hint_timeout = null

            const promises = []
            if (ass_uuids.length > 0) {
                promises.push(
                    backend_api_v2.post('constructibles/assemblies/get_list_hints/', ass_uuids)
                        .then(({ data }) => { this.list_hints = { ...this.list_hints, ...data } })
                        .catch(e => console.log(`Can't load assembly hints: ${e}`))
                )
            }
            if (mod_uuids.length > 0) {
                promises.push(
                    backend_api_v2.post('constructibles/modules/get_list_hints/', mod_uuids)
                        .then(({ data }) => { this.list_hints = { ...this.list_hints, ...data } })
                        .catch(e => console.log(`Can't load module hints: ${e}`))
                )
            }
            return Promise.all(promises)
        },

        // Multipanel mutations
        setMultipanelGroups: base.actions.setter("multipanel_groups"),
        createMultipanelGroup: base.actions.creator("multipanel_groups"),
        updateMultipanelGroup: base.actions.updater("multipanel_groups"),
        removeMultipanelGroup: base.actions.remover("multipanel_groups"),

        loadMultipanelGroups: base.actions.loader_pr("constructibles/multipanel_groups/", "MultipanelGroups"),
        postMultipanelGroup: base.actions.poster("constructibles/multipanel_groups/", "MultipanelGroup"),
        putMultipanelGroup: base.actions.putter("constructibles/multipanel_groups/", "MultipanelGroup"),
        deleteMultipanelGroup: base.actions.deleter("constructibles/multipanel_groups/", "MultipanelGroup"),

        loadMultipanelGroupsByBatch(batch_uuid) {
            return paginated_factory_v2(`constructibles/multipanel_groups/?batch=${batch_uuid}`)
                .then(({ data }) => {
                    // Merge into existing list (don't wipe other batches' groups)
                    const other = this.multipanel_groups.filter(g => g.batch !== batch_uuid)
                    this.setMultipanelGroups([...other, ...data])
                    return data
                })
                .catch(e => console.log(`Can't load multipanel groups: ${e}`))
        },

        async validateMultipanelGroup(assembly_uuids, gap_values, project_uuid) {
            return backend_api_v2.post('constructibles/multipanel_groups/validate/', {
                assembly_uuids,
                gap_values,
                project_uuid,
            })
        },

        async compatibleMultipanelPanels(assembly_uuids, batch_uuid) {
            return backend_api_v2.post('constructibles/multipanel_groups/compatible_panels/', {
                assembly_uuids,
                batch_uuid,
            })
        },

        async suggestMultipanelGroups(batch_uuid) {
            return backend_api_v2.post('constructibles/multipanel_groups/suggest/', { batch_uuid })
        },

        loadMyFirstConstructible(project_uuid) {
            // Register last call (ensure last called fills)
            LASTS["my_first_constructible"] = project_uuid

            return backend_api_v2.get('company/projects/' + project_uuid + '/first_constructible/')
                .then(({ data }) => {
                    // Reject prebounce
                    if (LASTS["my_first_constructible"] != project_uuid) {
                        console.log("[i] Rejecting first constructible commit. Not last call.")
                        return
                    }
                    if (data.is_a == "Assembly")
                        this.setAssemblies([data.assembly_list_object])
                    else if (data.is_a == "Module")
                        this.setModules([data.module_list_object])
                })
                .catch(e => { console.log(`Cant load first constructible: ${e}`) })
        },

    },
})
